import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LiverpoolCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Liverpool Pothole Claims | 9,547 Repairs in One Year | Fixtyer"
        description="Liverpool City Council filled 9,547 carriageway defects in 2024/25 — up from 1,680 five years earlier — while resurfacing collapsed to 11.8km and 2024 condition surveys were skipped at 50% staffing. See what the DfT AMBER rating means for your claim."
        canonical="/councils/liverpool"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Liverpool', url: '/councils/liverpool' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Liverpool spends nearly ten times its DfT capital allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but Liverpool is AMBER overall because road condition is declining — B and C roads in RED rose from 12% to 16% between 2020 and 2023, and unclassified roads hit 21% RED in 2022. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 1,116.48km — 78% of Liverpool\'s 1,435.25km carriageway network. RED-condition U-roads doubled from 11% in 2020/21 to 21% in 2022/23 before easing to 16% in 2023/24. There is no published network condition data at all for 2024/25 because surveys were not ordered.',
          },
          {
            question: 'Does the 50% staffing levels admission help my claim?',
            answer: 'Potentially, yes. Liverpool\'s own report states that in 2023 the highways service operated at approximately 50% staffing levels, which meant condition surveys were not ordered for 2024 reporting. That is a documented gap in the council\'s ability to know network condition — relevant to whether a Section 58 defence based on inspection records is reasonable for that period.',
          },
          {
            question: 'Pothole repairs jumped to 9,547 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The figures are for carriageway defects and repairs only, and they rose from roughly 1,700–2,000 per year in 2020/21 through 2022/23 to 7,629 in 2023/24 and 9,547 in 2024/25 — while structural resurfacing fell from 84.74km to 11.8km over the same period. High reactive repair counts signal a network producing defects faster than preventative work replaces surface layers.',
          },
          {
            question: 'Why is there no 2024 condition data for Liverpool\'s roads?',
            answer: 'Liverpool\'s transparency report records N/A for A, B/C and U-road condition in 2024. The council explains that condition surveys were not ordered for 2024 reporting because the highways service was operating at approximately 50% staffing levels in 2023, though surveys were subsequently ordered in the next financial year.',
          },
          {
            question: 'B and C roads: 72% amber or red by 2023 — what does that mean for claims?',
            answer: 'By 2023/24, 16% of Liverpool\'s B and C roads were in RED condition and 56% in AMBER — meaning 72% of the classified local road network was flagged as needing maintenance soon or immediately. The council\'s own definitions state RED means a road "should be considered for maintenance" and AMBER means "maintenance may be required soon".',
          },
        ]}
      />
      <ServiceSchema
        councilName="Liverpool City Council"
        councilSlug="liverpool"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/liverpool"
        headline="Liverpool Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Liverpool</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Liverpool: Record Reactive Repairs, Collapsing Resurfacing
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Liverpool City Council earns a GREEN spend scorecard — projected to invest
            <strong className="text-emerald-400"> £23.3m in capital maintenance in 2025/26</strong> against
            a DfT allocation of just £2.3m. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> because
            carriageway defects repaired jumped from <strong className="text-rose-400">1,680 to 9,547 in five years</strong>,
            structural resurfacing fell from 84.74km to 11.8km, and the council skipped 2024 condition surveys
            while operating at half staffing.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">9,547</div>
            <div className="text-white font-medium mt-2">Carriageway defects repaired in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 1,680 in 2020/21 — roughly 26 defect repairs every day across Liverpool's
              1,435km road network, while only 11.8km was structurally resurfaced.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Liverpool's own transparency report —
            classified roads flat or slipping, unclassified roads spiking
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (191km — 13.3% of network): stagnant</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">10%</div>
                <div className="text-sm text-rose-800">RED (2023/24)</div>
                <div className="text-xs text-surface-500">up from 9% in 2020/21</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">46%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 45% in 2020/21</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">44%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 46% in 2020/21</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Liverpool's main roads have barely moved in five years — and 56% of A-road length
              is now flagged amber or red. No 2024/25 data was published.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (128km — 8.9% of network): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">12%</td>
                    <td className="px-4 py-2 text-center">54%</td>
                    <td className="px-4 py-2 text-center">33%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">54%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">55%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">56%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads rose a third since 2020/21 (12% → 16%), and good-condition
              roads fell from 33% to 28%. By 2023/24, <strong>72% of B and C roads were amber or red</strong> —
              flagged for maintenance soon or immediately under the council's own definitions.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.3m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£23.3m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">84%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Liverpool spends roughly <span className="text-emerald-400">ten times its DfT allocation</span> on
              capital highway maintenance — and local road condition is still AMBER. The problem is not
              the headline spend figure. Reactive defect repairs are running at five times their 2020/21
              level while structural resurfacing has collapsed.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,116km Unclassified Majority
          </h2>
          <p className="text-surface-600 mb-8">
            78% of Liverpool's carriageway network is unclassified roads — and RED condition doubled in 2022/23
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data collected</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly One in Five U-Roads RED</h3>
              <p className="text-surface-600 text-sm mb-3">
                At the 2022/23 peak, 21% of Liverpool's unclassified roads were in RED condition —
                approximately <strong>234km of residential streets, estate roads and local routes</strong> flagged
                as roads that "should be considered for maintenance" under the council's own SCANNER definitions.
              </p>
              <p className="text-surface-600 text-sm">
                Even at the eased 16% figure in 2023/24, that is still roughly 179km of U-roads in
                RED — on a network class the council admits it is still building asset data for.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2024 Survey Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                Liverpool records N/A for all road classes in 2024/25. The council's explanation:
                staffing was at approximately 50% levels in 2023, so condition surveys were not
                ordered for 2024 reporting.
              </p>
              <p className="text-amber-800 text-sm">
                For incidents in 2024/25, there is <strong>no published network-level condition data</strong> —
                on the road type that makes up nearly four-fifths of the city's carriageways.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In 2023 the highways service was operating with approximately 50% staffing levels for a period of time. This meant that condition surveys were not ordered for 2024 reporting however they were subsequently ordered in the next financial year to enable the reporting to DfT to continue."
              source="Liverpool City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Liverpool's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it before or after the 2022/23 spike?</li>
              <li>• If up to 21% of U-roads were RED at the last comparable survey, what was done about yours?</li>
              <li>• How did half staffing in 2023 affect inspection and survey capacity?</li>
              <li>• Can the council claim detailed network knowledge for 2024/25 when it published no condition data at all?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim systematic knowledge of a network it failed to survey — and
              then produced record reactive repair volumes the following year.
            </p>
          </div>
        </div>
      </section>

      {/* Reactive repair explosion */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            22,593 Defect Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Pothole and carriageway defect repairs quintupled while structural resurfacing collapsed
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway defects repaired</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway resurfaced (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway preserved (km)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,680</td>
                  <td className="px-6 py-4 text-center">84.74</td>
                  <td className="px-6 py-4 text-center">258.14</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,025</td>
                  <td className="px-6 py-4 text-center">78.95</td>
                  <td className="px-6 py-4 text-center">271.78</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,712</td>
                  <td className="px-6 py-4 text-center">23</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">N/A</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,629</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10</td>
                  <td className="px-6 py-4 text-center">6.74</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9,547</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11.8</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">N/A</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22,593</td>
                  <td className="px-6 py-4 text-center font-bold" colSpan={2}>Structural work fell 86% from 2020/21 peak</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Reactive Spiral</h3>
              <p className="text-surface-600 text-sm">
                Liverpool's own footnote states the pothole figures are for carriageway defects and repairs.
                From 2023/24 onwards, repair volumes jumped to levels never seen in the prior three years —
                while resurfacing dropped from nearly 85km to around 11km. That is a network patching
                defects faster than it replaces failing surface courses.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Plans: More of the Same?</h3>
              <p className="text-amber-800 text-sm">
                Liverpool projects 7,528 pothole repairs in 2025/26 alongside just 7km of structural
                resurfacing and 2.5km of preventative preservation. The council plans 22,812m² of
                patching — reactive treatment at scale, not wholesale renewal.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="An ongoing priority is to transition the profile of spend towards planned preventative work, supported by life-cycle planning outputs in the Highways Asset Management Plan (HAMP), as a preventative maintenance strategy prolongs the life of pavement surface course layers and protects the original capital investment on these assets."
            source="Liverpool City Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Best practice gaps */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Liverpool invests heavily — but the council's own report flags gaps in data, prioritisation and survey continuity
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The desire is to continue to build up our asset information data in relation to inventory & condition to drive a data lead prioritisation of maintenance works across different assets with the desire to move towards a multi asset prioritisation process when planning long term programmes of work."
              source="Liverpool City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="We are currently in the process of refining this following a review of the current process and hierarchy to ensure it is fit for purpose and aligns with the council's priorities within the council plan."
              source="Liverpool City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">PAS 2161 Early Adopter</h3>
              <p className="text-surface-600 text-sm mb-3">
                Liverpool volunteered its network for PAS 2161 accreditation trials with TRL and the DfT,
                and plans Annual Engineer Inspections plus 360-camera surveys in 2025/26 — aiming for
                100% network coverage under the new methodology.
              </p>
              <p className="text-surface-600 text-sm">
                That is forward-looking ambition. It does not retroactively fill the 2024 survey gap
                or explain why reactive repairs quintupled while structural work collapsed.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Council's Own Definitions</h3>
              <p className="text-amber-800 text-sm mb-3">
                Liverpool publishes the SCANNER condition categories verbatim:
              </p>
              <ul className="text-amber-800 text-sm space-y-1">
                <li>• <strong>Green</strong> — No further investigation or treatment required</li>
                <li>• <strong>Amber</strong> — Maintenance may be required soon</li>
                <li>• <strong>Red</strong> — Should be considered for maintenance</li>
              </ul>
              <p className="text-amber-800 text-sm mt-3">
                By 2023/24, 72% of B/C roads and 16% of U-roads sat in the latter two categories.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Liverpool is not a low-spend authority — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests far beyond its DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 84% of projected 2025/26 spend classed as preventative
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented Highway Infrastructure Asset Management Strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Early PAS 2161 adopter with 100% survey coverage planned
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full permit scheme for streetworks coordination since 2018
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on major A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED up a third since 2020/21, 72% amber or red by 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED doubled to 21% in 2022/23 on 78% of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No 2024/25 condition data — surveys skipped at 50% staffing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 22,593 carriageway defect repairs in five years — defects outpace structural renewal
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing collapsed from 84.74km to 11.8km while repairs quintupled
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Asset data and prioritisation process still under review per the council's own report
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend scorecards, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 2022/23 RED spike and 2024 survey gap are your strongest structural arguments</li>
              <li>• Timing relative to the 2023 half-staffing period and the 2023/24 repair surge</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Liverpool's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Liverpool?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road condition spike argument</li>
                <li>• No prior-report search</li>
                <li>• No staffing-gap survey analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline and 72% amber-red rate documented</li>
                <li>✅ U-road 21% RED spike and 2024 survey gap argued</li>
                <li>✅ 22,593 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Liverpool</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=liverpool"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Liverpool Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Liverpool spends nearly ten times its DfT capital allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but Liverpool is AMBER overall because road condition
                is declining — B and C roads in RED rose from 12% to 16% between 2020 and 2023, and unclassified
                roads hit 21% RED in 2022. Section 58 turns on whether the specific defect that damaged your
                vehicle was reasonably inspected and repaired, not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,116.48km — 78% of Liverpool's 1,435.25km carriageway network.
                RED-condition U-roads doubled from 11% in 2020/21 to 21% in 2022/23 before easing to 16% in
                2023/24. There is no published network condition data at all for 2024/25 because surveys
                were not ordered.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 50% staffing levels admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, yes. Liverpool's own report states that in 2023 the highways service operated at
                approximately 50% staffing levels, which meant condition surveys were not ordered for 2024
                reporting. That is a documented gap in the council's ability to know network condition — relevant
                to whether a Section 58 defence based on inspection records is reasonable for that period.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs jumped to 9,547 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The figures are for carriageway defects and repairs only, and they rose from roughly
                1,700–2,000 per year in 2020/21 through 2022/23 to 7,629 in 2023/24 and 9,547 in 2024/25 —
                while structural resurfacing fell from 84.74km to 11.8km over the same period. High reactive
                repair counts signal a network producing defects faster than preventative work replaces surface layers.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is there no 2024 condition data for Liverpool's roads?
              </h3>
              <p className="text-surface-600 text-sm">
                Liverpool's transparency report records N/A for A, B/C and U-road condition in 2024. The council
                explains that condition surveys were not ordered for 2024 reporting because the highways service
                was operating at approximately 50% staffing levels in 2023, though surveys were subsequently
                ordered in the next financial year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                B and C roads: 72% amber or red by 2023 — what does that mean for claims?
              </h3>
              <p className="text-surface-600 text-sm">
                By 2023/24, 16% of Liverpool's B and C roads were in RED condition and 56% in AMBER — meaning
                72% of the classified local road network was flagged as needing maintenance soon or immediately.
                The council's own definitions state RED means a road "should be considered for maintenance" and
                AMBER means "maintenance may be required soon".
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://liverpool.gov.uk/highwaymaintenancetransparencyreport"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Liverpool City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
